/*
* Отдельное хранилище для заявок на Mobx
* Пришлось выделить для разделения логики, иначе вход в просмотр заявок клиента ломал список заявок
*/

import { action, observable, runInAction } from 'mobx';

import { ErrorInterface } from 'modules/core/api/globalApi';
import { orderController } from './orderController';
import {
  OrderInListInterface, TyreStorageOrder, UpdateOrderStatusRequest, TireFittingOrderItem,
  TireFittingOrdersRequest,
  LossAdjustmentOrderListItem, LossAdjustmentOrderListRequest, SchedulerBoardListRequest,
  HistoryV2Commit,
} from '../../api/orderApi';
import isEqual from 'lodash/isEqual';  
import sortBy from 'lodash/sortBy';

// Заказ клиента, выгруженный из 1С (ответ GET /v1/orders/{clientId}/1c).
// Поля по договорённости с бэком — все опциональны.
export interface Order1CItem {
  guid?: string,
  name?: string,
  count?: number,
  price?: number,
  sum?: number,
}

export interface Order1C {
  items?: Order1CItem[],
  ordernumber?: string,
  orderdate?: string,
  ordersum?: number,
  orderstatus?: string,
}
export interface OrderStore {
  clientOrders: OrderInListInterface[],
  clientOrdersByClientId: string,

  tyreStorageOrders: TyreStorageOrder[],
  tyreStorageOrdersByClientId: string,
  loadingTyreStorageOrders: boolean,

  // Комплект колёс на хранении по конкретному orderId
  // сам orderId есть внутри (поле id)
  // извлекается отдельным методом
  currentTyreStorageOrder: TyreStorageOrder,

  tireFittingOrders: TireFittingOrderItem[],
  tireFittingOrdersRequest: TireFittingOrdersRequest,
  tireFittingOrdersFilter: TireFittingOrdersRequest,
  
  lossAdjustmentOrders: LossAdjustmentOrderListItem[],
  lossAdjustmentOrderListRequest: LossAdjustmentOrderListRequest,
  lossAdjustmentOrderListFilter: LossAdjustmentOrderListRequest,

  schedulerBoardList: SchedulerBoardListRequest[],
  schedulerBoardListOfficeId: string, 

  orderHistoryV2: HistoryV2Commit[],
  orderHistoryV2OrderId: string,

  clientOrders1C: Order1C[],
  clientOrders1CByClientId: string,

  error: ErrorInterface,
}

export const orderStore: OrderStore = observable({
  clientOrders: [],
  clientOrdersByClientId: undefined,

  tyreStorageOrders: [],
  tyreStorageOrdersByClientId: null,
  loadingTyreStorageOrders: false,

  currentTyreStorageOrder: null,

  tireFittingOrders: [],
  tireFittingOrdersRequest: null,

  lossAdjustmentOrders: [],
  lossAdjustmentOrderListRequest: null,
  lossAdjustmentOrderListFilter: null,

  schedulerBoardList: [],
  schedulerBoardListOfficeId: null, 

  orderHistoryV2: [],
  orderHistoryV2OrderId: null,

  clientOrders1C: [],
  clientOrders1CByClientId: undefined,

  error: null,
} as OrderStore);

export const orderActions = {

  getOrdersByClient: async (clientId:string): Promise<void> => {
    orderStore.error = null;
    orderStore.clientOrdersByClientId = clientId;
    const response = await orderController.getOrdersByClient({
      clientId,
      offset: 0,
      size: 500,
      sortField: 'createdAt',
      sortOrder: 'DESC',
    });
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.clientOrders = response?.data?.orders ?? [];
      });
    } else {
      orderStore.clientOrdersByClientId = undefined;
      orderStore.clientOrders = [];
    }
  },

  clearOrdersByClient: action(() => {
    orderStore.clientOrders = [];
    orderStore.clientOrdersByClientId = undefined;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { data } = response;
      orderStore.error = data;
      throw data;
    }
  }),

  createLinkedOrder: async (orderId:string, serviceId: number): Promise<string> => {
    const response = await orderController.createLinkedOrder(orderId, serviceId);
    orderActions.handleError(response);
    return response?.data?.orderId;
  },

  getTyreInStorageOrders: async (clientId:string): Promise<void> => {
    orderStore.error = null;
    runInAction(() => {
      orderStore.tyreStorageOrdersByClientId = clientId;
      orderStore.loadingTyreStorageOrders = true;
    });
    const response = await orderController.getTyreInStorageOrders(clientId);
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.tyreStorageOrders = response?.data?.items ?? [];
      });
      runInAction(() => {
        orderStore.loadingTyreStorageOrders = false;
      });
    } else {
      orderStore.tyreStorageOrdersByClientId = undefined;
      orderStore.tyreStorageOrders = [];
      orderStore.loadingTyreStorageOrders = false;
    }
  },

  getTyreInStorageByOrderId: async (orderId:string): Promise<void> => {
    orderStore.error = null;
    const response = await orderController.getTyreInStorageByOrderId(orderId);
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.currentTyreStorageOrder = response?.data ?? null;
      });
    } else {
      orderStore.currentTyreStorageOrder = null;
    }
  },

  updateOrderStatus: async (request: UpdateOrderStatusRequest): Promise<void> => {
    const response = await orderController.updateOrderStatus(request);
    orderActions.handleError(response);
    if (!response.errorData) {
      const x = orderStore.tyreStorageOrders.findIndex((v) => v.id === request.id);
      orderStore.tyreStorageOrders[x].statusRefValueId = response?.data?.statusRefValueId;
      orderStore.tyreStorageOrders[x].statusRefValueName = response?.data?.statusRefValueName;
      orderStore.tyreStorageOrders = [...orderStore.tyreStorageOrders];
    }
    // orderActions.setCurrentInvoice(response.data);
  },

  clearTyreStorageOrders: action(() => {
    orderStore.tyreStorageOrders = [];
    orderStore.tyreStorageOrdersByClientId = null;
    orderStore.currentTyreStorageOrder = null;
  }),

  clearCurrentTyreStorageOrder: action(() => {
    orderStore.currentTyreStorageOrder = null;
  }),

  getTireFittingOrders: async (request: TireFittingOrdersRequest): Promise<void> => {
    orderStore.error = null;
    runInAction(() => {
      orderStore.tireFittingOrdersRequest = request;
    });
    const response = await orderController.getTireFittingOrders(request);
    orderActions.handleError(response);
    if (!response.errorData) {
      orderStore.tireFittingOrders = response.data.items.sort((a, b) => (b.createdDate - a.createdDate)) ?? [];
    }
    else {
      orderStore.tireFittingOrdersRequest = null;
      orderStore.tireFittingOrders = [];
    }
  },

  clearTireFittingOrders: action(() => {
    orderStore.tireFittingOrders = [];
    orderStore.tireFittingOrdersRequest = null;
  }),

  setTireFittingFilters: async (value: TireFittingOrdersRequest): Promise<void> => {
    if (isEqual(orderStore.tireFittingOrdersFilter, value)) {
      return;
    }
    runInAction(() => {
      orderStore.tireFittingOrdersFilter = value;
    });
  },

  getLossAdjustmentOrders: async (request: LossAdjustmentOrderListRequest): Promise<void> => {
    orderStore.error = null;
    runInAction(() => {
      orderStore.lossAdjustmentOrderListRequest = request;
    });
    const response = await orderController.getLossAdjustmentOrders(request);
    orderActions.handleError(response);
    if (!response.errorData) {
      orderStore.lossAdjustmentOrders = response.data.items.sort((a, b) => (b.createdDate - a.createdDate)) ?? [];
    }
    else {
      orderStore.lossAdjustmentOrderListRequest = null;
      orderStore.lossAdjustmentOrders = [];
    }
  },

  clearLossAdjustmentOrders: action(() => {
    orderStore.lossAdjustmentOrders = [];
    orderStore.lossAdjustmentOrderListRequest = null;
  }),

  setLossAdjustmentFilter: async (value: LossAdjustmentOrderListRequest): Promise<void> => {
    if (isEqual(orderStore.lossAdjustmentOrderListFilter, value)) {
      return;
    }
    runInAction(() => {
      orderStore.lossAdjustmentOrderListFilter = value;
    });
  },

  getSchedulerBoardList: async (partnerOfficeId:string): Promise<void> => {
    orderStore.error = null;
    runInAction(() => {
      orderStore.schedulerBoardListOfficeId = partnerOfficeId;
    });
    const response = await orderController.getSchedulerBoardList(partnerOfficeId);
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.schedulerBoardList = response?.data?.items ?? [];
      });
    } else {
      orderStore.schedulerBoardListOfficeId = undefined;
      orderStore.schedulerBoardList = [];
    }
  },

  clearSchedulerBoardList: action(() => {
    orderStore.schedulerBoardList = [];
    orderStore.schedulerBoardListOfficeId = null;
  }),

  getOrderHistoryV2: async (orderId: string): Promise<void> => {
    orderStore.error = null;
    runInAction(() => {
      orderStore.orderHistoryV2OrderId = orderId;
    });
    const response = await orderController.getOrderHistoryV2(orderId);
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.orderHistoryV2 = sortBy(response?.data, 'commitId') ?? [];
      });
    } else {
      orderStore.orderHistoryV2OrderId = null;
      orderStore.orderHistoryV2 = [];
    }
  },

  clearOrderHistoryV2: action(() => {
    orderStore.orderHistoryV2OrderId = null;
    orderStore.orderHistoryV2 = [];
  }),

  getOrders1C: async (clientId: string): Promise<void> => {
    orderStore.error = null;
    orderStore.clientOrders1CByClientId = clientId;
    const response = await orderController.getOrders1C(clientId);
    orderActions.handleError(response);
    if (!response.errorData) {
      runInAction(() => {
        orderStore.clientOrders1C = response?.data?.orders ?? [];
      });
    } else {
      orderStore.clientOrders1CByClientId = undefined;
      orderStore.clientOrders1C = [];
    }
  },

  clearOrders1C: action(() => {
    orderStore.clientOrders1C = [];
    orderStore.clientOrders1CByClientId = undefined;
  }),

};
